/**
 * @file sectionSort.ts
 * @brief U5: where a dragged Intel / Points section lands, and where every
 *        other section slides to while it is dragged (pure).
 *
 * @details
 * DES 03-11-2026: "Home screen sections drag, here they don't". The phone's
 * Home drags a section with react-native-sortables: the section lifts, the
 * others slide out of its way as it moves, and it lands in the gap. Web Home
 * has no drag of its own (it follows the phone's order), so the web copies
 * the PHONE Home's feel. While dragging, every section of the tab is folded
 * to its heading, so the rows measured here are headings (U5 point 3).
 *
 * Rows are measured once, at rest, in DOM order. A row with a `key` moves;
 * a row without one (a Points topic with a plain heading, the points without
 * a topic) keeps its slot, exactly as `placeKeyed` keeps it.
 *
 * @references
 * - ./useSectionSort.ts (the pointer side), ./intelLayout.ts (dropSection)
 * - FRONTEND src/screens/Home/HomeScreen.tsx (Sortable.Grid, the look)
 *
 * @author DES
 * @date 03-11-2026
 */

export interface SortRow {
  /** The unit's first key; null = a row that never moves. */
  key: string | null;
  /** Top in scroll-content pixels, at rest. */
  top: number;
  /** Its height plus the gap to the next row (top of next − top). */
  stride: number;
}

export interface SortPlan {
  /** The moving row the dragged one takes the place of (for dropSection);
   *  null = it stays where it is. */
  targetKey: string | null;
  /** Where each row would sit (scroll-content top) if dropped now. */
  tops: number[];
}

/** The rows' tops when the movable rows are drawn in `order`. */
function layOut(rows: readonly SortRow[], order: readonly string[]): number[] {
  const byKey = new Map(rows.flatMap((r) => (r.key ? [[r.key, r] as const] : [])));
  let slot = 0;
  let y = rows[0]?.top ?? 0;
  const tops = new Map<SortRow, number>();
  for (const row of rows) {
    const drawn = row.key ? (byKey.get(order[slot++]) as SortRow) : row;
    tops.set(drawn, y);
    y += drawn.stride;
  }
  return rows.map((r) => tops.get(r) as number);
}

/**
 * The slot whose top is nearest the dragged row's current top wins (the
 * phone's sortable picks the slot the item's centre is over; with headings
 * of one height, the same thing).
 *
 * @param draggedTop the dragged row's top now, in scroll-content pixels
 */
export function planSort(rows: readonly SortRow[], draggedKey: string, draggedTop: number): SortPlan {
  const keys = rows.flatMap((r) => (r.key ? [r.key] : []));
  const from = keys.indexOf(draggedKey);
  const at = rows.findIndex((r) => r.key === draggedKey);
  if (from < 0) return { targetKey: null, tops: rows.map((r) => r.top) };
  const rest = keys.filter((k) => k !== draggedKey);
  let best = { slot: from, tops: rows.map((r) => r.top), gap: Infinity };
  for (let slot = 0; slot < keys.length; slot++) {
    const order = [...rest.slice(0, slot), draggedKey, ...rest.slice(slot)];
    const tops = layOut(rows, order);
    const gap = Math.abs(tops[at] - draggedTop);
    if (gap < best.gap) best = { slot, tops, gap };
  }
  return { targetKey: best.slot === from ? null : keys[best.slot], tops: best.tops };
}

/** Rows from measured boxes (DOM order): stride = distance to the next
 *  row's top; the last row's = its own height + `lastGap`. */
export function rowsFromBoxes(
  boxes: readonly { key: string | null; top: number; height: number }[],
  lastGap = 0,
): SortRow[] {
  return boxes.map((b, i) => ({
    key: b.key,
    top: b.top,
    stride: i < boxes.length - 1 ? boxes[i + 1].top - b.top : b.height + lastGap,
  }));
}
